import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useAssembly, useUpdateAssembly, useVoidAssembly } from '@/api/assemblies'
import { useAssemblyAuditLogs } from '@/api/audit-logs'
import type { AssemblyDetail, AssemblyGroup, AssemblyLine, AssemblyMovement } from '@/api/assemblies'
import { useIdempotencyKey } from '@/api/idempotency'
import { useWorkspace } from '@/api/workspace'
import { Figure } from '@/components/batches/batch-parts'
import { DependentRecords } from '@/components/corrections/dependent-records'
import { correctionErrorMessage } from '@/components/corrections/error-message'
import { NoteCard } from '@/components/corrections/note-card'
import type { NoteSave } from '@/components/corrections/note-card'
import { VoidDialog } from '@/components/corrections/void-dialog'
import { VoidedBadge, VoidedNotice } from '@/components/corrections/voided'
import { voidedClass } from '@/components/corrections/voided-record'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney, formatQty } from '@/components/materials/pool-format'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordHistory } from '@/components/record-history/record-history'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'

// Assembly detail (UI-SCREENS F-11, #134, #135): the Variant, qty, labor minutes, the BOM version
// used, the lines grouped by Model / Pool (expected once, actual per stock item, substitute) and the
// movements with their values (cost figures are shown to Staff too), a void's reversals linked to
// the rows they negate. The Owner voids it (the dialog lists the records to void first when it is
// refused); the note stays editable, voided or not (Owner any, Staff own).
export const Route = createFileRoute('/w/$slug/assemblies/$assemblyId/')({
  component: AssemblyDetailScreen,
  staticData: { breadcrumb: 'assemblies.detail.title' },
})

function AssemblyDetailScreen() {
  const { t } = useTranslation()
  const { slug, assemblyId } = Route.useParams()
  const id = Number(assemblyId)
  const assembly = useAssembly(slug, id)
  // The Owner-only History section (#137): void, note edits, backdate overrides.
  const isOwner = useWorkspace(slug).data?.role === 'owner'
  const history = useAssemblyAuditLogs(slug, id, { enabled: isOwner })
  const voidAssembly = useVoidAssembly()
  const updateAssembly = useUpdateAssembly()
  const idempotency = useIdempotencyKey()

  if (assembly.isPending) return <LoadingSkeleton variant="page" />
  if (assembly.isError || !assembly.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  const data: AssemblyDetail = assembly.data
  const createdBy = data.created_by ? (data.created_by.name ?? data.created_by.email) : '—'

  // One Idempotency-Key per submission; the void is a transition, so it carries one.
  const onVoid = (reason: string, done: () => void) => {
    const body = { void: { reason } }
    voidAssembly.mutate(
      { params: { path: { slug, assembly_id: id }, header: idempotency.headerFor(body) }, body },
      { onSuccess: done, onSettled: (_data, error) => idempotency.settle(error) },
    )
  }

  const onSaveNote = (note: string | null, save: NoteSave) => {
    updateAssembly.mutate(
      { params: { path: { slug, id } }, body: { assembly: { note, lock_version: save.lockVersion } } },
      { onSuccess: save.done, onSettled: (_data, error) => save.settle(error) },
    )
  }

  return (
    <section className={`flex max-w-3xl flex-col gap-4 ${voidedClass(Boolean(data.voided_at))}`}>
      <Link to="/w/$slug/assemblies" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('assemblies.back')}
      </Link>
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="font-heading text-xl font-semibold">
          {data.variant.sku} · {data.variant.name}
        </h1>
        <VoidedBadge record={data} />
        {data.can.void ? (
          <div className="ml-auto">
            <VoidDialog
              onVoid={onVoid}
              pending={voidAssembly.isPending}
              errorMessage={voidAssembly.isError ? correctionErrorMessage(t, voidAssembly.error) : ''}
              onReset={voidAssembly.reset}
              blocked={voidAssembly.isError ? <DependentRecords slug={slug} error={voidAssembly.error} /> : null}
            />
          </div>
        ) : null}
      </div>
      <VoidedNotice record={data} />

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
        <Figure label={t('assemblies.columns.qty')} value={formatQty(data.qty)} />
        <Figure label={t('assemblies.columns.laborMinutes')} value={t('assemblies.minutes', { count: data.labor_minutes })} />
        <Figure label={t('assemblies.columns.date')} value={formatBusinessDate(data.occurred_on)} />
        <Figure
          label={t('assemblies.detail.bom')}
          value={t('assemblies.detail.bomValue', { date: formatBusinessDate(data.bom.effective_from) })}
        />
        <Figure label={t('assemblies.columns.createdBy')} value={createdBy} />
      </dl>

      <NoteCard
        note={data.note}
        lockVersion={data.lock_version}
        canEdit={data.can.edit_note}
        pending={updateAssembly.isPending}
        errorMessage={updateAssembly.isError ? correctionErrorMessage(t, updateAssembly.error) : ''}
        onSave={onSaveNote}
        onReset={updateAssembly.reset}
      />

      <h2 className="font-heading text-lg font-semibold">{t('assemblies.detail.lines')}</h2>
      <ul className="flex flex-col gap-3">
        {data.groups.map((group) => (
          <li key={group.line_ids.join('-')}>
            <GroupCard group={group} lines={data.lines.filter((line) => group.line_ids.includes(line.id))} />
          </li>
        ))}
      </ul>

      {data.movements.length > 0 ? (
        <>
          <h2 className="font-heading text-lg font-semibold">{t('assemblies.detail.movements')}</h2>
          <Card>
            <CardContent className="py-4">
              <ul className="flex flex-col divide-y">
                {data.movements.map((movement) => (
                  <li key={movement.id} className="py-3 first:pt-0 last:pb-0">
                    <MovementRow assembly={data} movement={movement} />
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </>
      ) : null}

      {isOwner ? <RecordHistory entries={history.data} isPending={history.isPending} isError={history.isError} /> : null}
    </section>
  )
}

// One BOM line: its Model or Pool with the expected qty once (Assembly#expected_by_group), and a
// row per stock item used (a Model split over WIP items; colors, actual, substitute).
function GroupCard({ group, lines }: { group: AssemblyGroup; lines: AssemblyLine[] }) {
  const { t } = useTranslation()
  const unit = group.pool ? ` ${group.pool.unit}` : ''
  const split = lines.length > 1

  return (
    <Card>
      <CardContent className="flex flex-col gap-2 py-4">
        <span className="font-medium">{group.pool ? group.pool.label : (group.model?.name ?? '—')}</span>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <Figure label={t('assemblies.detail.expected')} value={`${formatQty(group.expected_qty)}${unit}`} />
          <Figure
            label={t(split ? 'assemblies.detail.total' : 'assemblies.detail.actual')}
            value={`${formatQty(group.actual_qty)}${unit}`}
          />
        </dl>
        {lines.some((line) => line.colors) || split ? (
          <ul className="flex flex-col gap-2">
            {lines.map((line) => (
              <li key={line.id} className="flex flex-col gap-1 rounded-lg border p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-muted-foreground">
                    {(line.colors ?? []).map((color) => `${color.label}: ${color.pool.label}`).join(', ')}
                  </span>
                  {line.substitute ? <Badge variant="outline">{t('assemblies.detail.substitute')}</Badge> : null}
                </div>
                <Figure label={t('assemblies.detail.actual')} value={`${formatQty(line.actual_qty)}${unit}`} />
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  )
}

// A WIP part taken (−parts), a component deduction (−pcs / −value) or the Finished Goods added. A
// void adds the exact negation of each row, linked to it by `reverses_movement_id` (UI-SCREENS §2).
function MovementRow({ assembly, movement }: { assembly: AssemblyDetail; movement: AssemblyMovement }) {
  const { t } = useTranslation()
  const unit = movement.pool ? ` ${movement.pool.unit}` : ''
  const wipLine = assembly.lines.find((line) => line.stock_item_id === movement.stock_item_id)
  const label = movement.pool
    ? movement.pool.label
    : movement.stock_item_kind === 'variant'
      ? `${t('assemblies.detail.movementFinishedGoods')} · ${assembly.variant.sku}`
      : `${t('assemblies.detail.movementWip')} · ${wipLine?.model?.name ?? ''}`

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{label}</span>
        {movement.reverses_movement_id ? (
          <Badge variant="outline">{t('corrections.reversalOf', { id: movement.reverses_movement_id })}</Badge>
        ) : null}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <Figure label={t('assemblies.detail.qtyDelta')} value={`${signed(movement.qty_delta, formatQty)}${unit}`} />
        <Figure
          label={t('assemblies.detail.valueDelta')}
          value={movement.value_delta === null ? '—' : `Rp ${signed(movement.value_delta, formatMoney)}`}
        />
      </dl>
    </div>
  )
}

const signed = (value: number, format: (value: number) => string) =>
  `${value > 0 ? '+' : value < 0 ? '−' : ''}${format(Math.abs(value))}`
